import React from "react";
import { cn } from "@/lib/utils";
import Loading from "@/components/Loading";
import { Button } from "@/components/fun-ui/Button";
import { components } from "@/components/md-components";
import { IconButton } from "@/components/icon-button";
import { FeedbackIcon } from "@/components/fun-ui/icons";
import { ExtractImage } from "@/stores/slices/paperSlice";
import { ExtractChatView } from "./ExtractChatView";
import ImageDownloader from "./ImageDownloader";
import { Feedback } from "@/components/dashboard/Feedback";
import { CopyCellText } from "./CopyCellText";
import { MarkdownRenderer } from "../PaperDetail/MarkdownRenderer";
import { checkIsExceedMaxPages, ExceedMaxPages } from "../PictureTypePaperMaxPagesTip";
import { isMembershipUpgradeTab } from "@/lib/payment";
import { getExtractChartFallbackBasename, sanitizeExtractChartBasename } from "./extractChartDownloadFilename";


export function ResultView({
  keyName,
  taskId,
  result: _result,
  status,
  extractStatus,
  onRetry,
  onRefresh,
  isPolling,
  className = '',
  paperName,
  images = [],
  tables: _tables = [],
  attachmentId,
}: {
  keyName: string;
  paperName: string;
  taskId?: string;
  result?: string;
  status?: number;
  extractStatus?: number;
  onRetry: () => void;
  onRefresh: () => void;
  isPolling: boolean;
  className?: string;
  images?: ExtractImage[];
  tables?: ExtractImage[];
  attachmentId?: string | number;
}) {
  let result = _result;
  const tables = _tables.map((item) => ({
    ...item,
    imagePath: item.imagePath || item.imagePage || '',
  }));

  const isBasicInfo = ['information', 'organization', 'author', 'createAt', 'journal'].includes(keyName);

  if (attachmentId?.toString() === '0' && !isBasicInfo) {
    return (
      <div className={cn(
        "min-w-[400px] h-full text-annotation-foreground",
        className,
      )}>
        <div className="absolute inset-5 flex items-center justify-center text-muted-foreground text-sm">
          摘要型文献暂不支持 AI 内容提取
        </div>
      </div>
    );
  }

  if (extractStatus && extractStatus < 0) {
    // -30013   非会员，50页内的图片型文献
    // -30015   50页以上的图片型文献
    if (checkIsExceedMaxPages(extractStatus)) {
      return (
        <div
          className={cn(
            "min-w-[400px] h-full text-annotation-foreground",
            className,
          )}
        >
          <div className="absolute top-5 left-5 right-5 flex items-center">
            <ExceedMaxPages
              extractStatus={extractStatus}
              callback={({ type, status }) => {
                if (isMembershipUpgradeTab(type) && status === 'success') {
                  onRefresh();
                }
              }}
            />
          </div>
        </div>
      );
    }
    return (
      <div className={cn(
        "min-w-[400px] h-full text-annotation-foreground",
        className,
      )}>
        <div className="absolute inset-5 flex items-center">
          {result}
        </div>
      </div>
    );
  } else if (
    typeof status !== 'undefined' &&
    (status < 0 || (status >= 0 && status !== 2 && !isPolling)
    )) {
    if (status >= 0 && status !== 2 && !isPolling) {
      result = '数据获取中，请刷新';
    }
    return (
      <div className={cn(
        "min-w-[400px] h-full",
        className,
      )}>
        <div className="absolute top-5 flex items-center">
          <span>{result || 'AI 生成失败，请重试'}</span>
          <Button
            variant="outline"
            className="h-6 w-fit px-2 ml-2 border-primary text-primary hover:text-primary hover:bg-primary/10 rounded-full"
            onClick={onRetry}
          >
            刷新
          </Button>
        </div>
      </div>
    );
  } else if (status === 2 && result) {
    return (
      <>
        <MarkdownRenderer
          className={cn(
            "text-justify min-w-[400px]",
            className,
          )}
          components={components}
        >{result}</MarkdownRenderer>
        <CellToolbar
          keyName={keyName}
          paperName={paperName}
          taskId={taskId}
          result={result}
          images={images}
          tables={tables}
        />
      </>
    );
  } else if (status === 2 && keyName === 'extractChart') {
    return (
      <>
        <ExtractChatView images={images.concat(tables)} />
        <CellToolbar
          keyName={keyName}
          paperName={paperName}
          taskId={taskId}
          result={result}
          images={images}
          tables={tables}
        />
      </>
    )
  }
  if (typeof status === 'undefined') {
    result = '数据加载中，请稍候';
  }
  return (
    <div className={cn(
      "min-w-[400px] h-full",
      className,
    )}>
      <Loading
        className="top-4 translate-y-0"
        size="sm"
      >{result || (keyName === 'extractChart' ? '文献解析中，每篇大约需要 1~2 分钟，请稍候' : 'AI 生成中，大约需要 1~2 分钟')} </Loading>
    </div>
  );
}

const CellToolbar = ({
  keyName,
  paperName,
  taskId = '',
  images = [],
  tables = [],
  result = '',
}: {
  keyName: string;
  paperName: string;
  taskId?: string;
  images?: ExtractImage[];
  tables?: ExtractImage[],
  result?: string;
}) => {
  return (
    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full bg-background rounded-md border border-border border-solid p-1 flex items-center justify-center gap-1 opacity-0 pointer-events-none group-hover/cell:opacity-100 group-hover/cell:pointer-events-auto transition-opacity duration-200 z-20">
      <ImageDownloader
        images={
          images.map(({ imagePath, caption }) => (
            {
              url: imagePath,
              name: caption || '',
            }
          )).concat(tables.map(({ imagePath, caption }) => (
            {
              url: imagePath,
              name: caption || '',
            }
          )))
        }
        zipName={sanitizeExtractChartBasename(`Findin图表提取_${paperName}`) || getExtractChartFallbackBasename()}
      />
      <CopyCellText text={result} />
      <Feedback scene={keyName === 'information' ? 'aiColBasicCell' : 'aiColCell'} sceneContents={{
        scene_id: taskId,
      }}>
        <IconButton label="反馈" >
          <FeedbackIcon />
        </IconButton>
      </Feedback>
      <div className="absolute top-full left-1/2 -translate-x-1/2 flex justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" fill="none" viewBox="0 0 18 8" className="w-[18px] h-[8px]">
          <g>
            <path d="M0,-4L7.5134115,4.3482351C8.3078957,5.2309961000000005,9.6921043,5.2309961000000005,10.486588,4.3482351L18,-4L0,-4Z" fill="#FFFFFF" fillOpacity="1" />
            <path d="M10.858235,4.6827173L19.122681,-4.49999997L-1.1226813,-4.49999997L7.1417646,4.6827173Q7.8865938,5.510306399999999,9,5.510306399999999Q10.113406,5.510306399999999,10.858235,4.6827173ZM16.877319,-3.50000003L10.114941,4.0137529Q9.6680431,4.5103054,9,4.5103054Q8.3319559,4.5103054,7.8850594,4.0137529L1.1226811,-3.50000003L16.877319,-3.50000003Z" fillRule="evenodd" fill="#E5E6EB" fillOpacity="1" />
          </g>
        </svg>
      </div>
    </div >
  )
}
